import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  prequalificationSentLine,
  sendPrequalificationLetter,
} from "@/lib/accountActions";
import { isEmailShaped } from "@/lib/email";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { ActionFormDialog } from "@/components/ActionFormDialog";
import { TextField } from "@/components/TextField";

// BRK-S405 — "Send pre-qualification letter": PMS emails a letter confirming
// the insured is pre-qualified under the program it holds (PMS-S847, pinned
// at BRK-S511). ONE component, mounted by the account's program buttons
// (SuretyProgramBlock) and the submission's Surety Program tab
// (SubmissionProgramTab), each with the insured's account id.
//
// "Sent to {email}" is said only from PMS's 2xx (NN #7); a refusal renders
// inline with what was typed kept. The program block only offers the button on
// a program in force, and PMS decides whether a letter can go (409
// NO_PROGRAM_IN_FORCE, rendered as the summary): this dialog checks nothing but
// the shape of the address.

const FIELDS = ["email", "obligeeName"] as const;

export function SendPrequalificationDialog({
  open,
  onClose,
  accountId,
}: {
  open: boolean;
  onClose: () => void;
  accountId: string;
}) {
  const { authedRequest } = useAuth();
  const [email, setEmail] = useState("");
  const [obligee, setObligee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);

  // Fresh each time it opens.
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setObligee("");
    setError(null);
    setInvalid({});
    setSuccess(null);
  }, [open]);

  async function send() {
    const to = email.trim();
    const missing: Record<string, string> = {};
    if (to === "") missing.email = "Enter the recipient’s email address.";
    else if (!isEmailShaped(to)) missing.email = "Enter a valid email address.";
    setInvalid(missing);
    setError(null);
    if (Object.keys(missing).length > 0) return;
    setBusy(true);
    try {
      const receipt = await sendPrequalificationLetter(
        authedRequest,
        accountId,
        { email: to, obligeeName: obligee },
      );
      setSuccess(prequalificationSentLine(receipt, to));
    } catch (err) {
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionFormDialog
      open={open}
      onClose={onClose}
      title="Send pre-qualification letter"
      lead="We’ll email a letter confirming this insured is pre-qualified under their surety program."
      submitLabel="Send letter"
      busy={busy}
      error={error}
      success={success}
      onSubmit={() => void send()}
    >
      <TextField
        label="Send to"
        type="email"
        autoComplete="off"
        value={email}
        required
        onChange={(e) => setEmail(e.target.value)}
        error={invalid.email}
      />
      <TextField
        label="Obligee"
        optional
        autoComplete="off"
        value={obligee}
        onChange={(e) => setObligee(e.target.value)}
        error={invalid.obligeeName}
      />
    </ActionFormDialog>
  );
}
